import { hexToRgba, lerp } from "@/lib/utils";
import { useCallback, useEffect, useRef } from "react";

type DynamicAnimatedWaveProps = React.DetailedHTMLProps<
  React.CanvasHTMLAttributes<HTMLCanvasElement>,
  HTMLCanvasElement
> & {
  colors?: string[]; // colors for the waveform
  amplitude?: number; // amplitude of the waveform
};

const DEFAULT_WAVE_COLORS = ["#1e4c76", "#31cfff", "#1e4c76"];

export function DynamicAnimatedWave({
  amplitude = 20, // default amplitude if none provided
  colors = DEFAULT_WAVE_COLORS,
  ...props
}: DynamicAnimatedWaveProps) {
  const amplitudeRef = useRef(amplitude);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const targetAmplitudeRef = useRef<number>(amplitude);

  // Update the target amplitude whenever the amplitude prop changes
  useEffect(() => {
    targetAmplitudeRef.current = amplitude;
  }, [amplitude]);

  // Function to draw the waveform on the canvas
  const drawWaveform = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      _amplitude: number,
      frequency: number,
      color: string | CanvasGradient,
      phase = 0,
    ) => {
      ctx.strokeStyle = color;
      ctx.beginPath();

      // Draw the waveform
      for (let i = 0; i < ctx.canvas.width; i++) {
        // Compute the pinching effect
        const sineWave = Math.sin(Math.PI * (i / ctx.canvas.width));
        const pinch = sineWave ** 6;

        // Calculate the y position with sine function, pinch effect, and time-based phase shift
        const y = _amplitude * Math.sin(frequency * i + phase) * pinch;

        ctx.lineTo(i, ctx.canvas.height / 2 + y);
      }

      ctx.stroke();
    },
    [],
  );

  // Animate the waveform
  useEffect(() => {
    let animationFrameId: number;
    const canvas = canvasRef.current as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    const parent = canvas.parentElement;
    if (parent) {
      canvas.width = parent.clientWidth;
    }

    const animate = () => {
      // Convert milliseconds to seconds and increase speed
      const time = Date.now() * 0.0015;

      amplitudeRef.current = lerp(
        amplitudeRef.current,
        targetAmplitudeRef.current,
        0.1,
      );

      // Create an oscillating effect with amplitude
      const oscillatingAmplitude =
        amplitudeRef.current * (1 + Math.sin(time) * 0.05);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Define primary waveforms with their respective amplitudes, frequencies, and colors
      const primaryWaveforms = [
        {
          amplitude: oscillatingAmplitude,
          frequency: 0.02,
          alpha: 0.6,
          speed: 0.001,
        },
        {
          amplitude: oscillatingAmplitude * 0.6,
          frequency: 0.03,
          alpha: 0.4,
          speed: 0.004,
        },
        {
          amplitude: oscillatingAmplitude * 0.3,
          frequency: 0.04,
          alpha: 0.2,
          speed: 0.007,
        },
      ];

      // For each primary waveform, draw the waveform and its surrounding mesh
      for (const primary of primaryWaveforms) {
        const gradient = ctx.createLinearGradient(
          0,
          0,
          canvas.width,
          canvas.height,
        );
        const stopIncrement = colors.length > 1 ? 1 / (colors.length - 1) : 0;
        colors.forEach((color, index) => {
          gradient.addColorStop(
            stopIncrement * index,
            hexToRgba(color, primary.alpha),
          );
        });

        drawWaveform(
          ctx,
          primary.amplitude,
          primary.frequency,
          gradient,
          Date.now() * primary.speed,
        );

        // Draw secondary waveforms around the primary waveform
        for (let i = 0; i < 30; i++) {
          const amp = primary.amplitude - i * 0.1;
          const freq = primary.frequency + i * 0.00025;
          const alpha = primary.alpha * 0.6 - i * 0.01;
          const meshGradient = ctx.createLinearGradient(
            0,
            0,
            canvas.width,
            canvas.height,
          );
          const meshStopIncrement =
            colors.length > 1 ? 1 / (colors.length - 1) : 0;
          colors.forEach((color, index) => {
            meshGradient.addColorStop(
              meshStopIncrement * index,
              hexToRgba(color, alpha),
            );
          });

          drawWaveform(
            ctx,
            amp,
            freq,
            gradient,
            Date.now() * primary.speed + i * 0.015,
          );
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Clean up the animation frame when the component unmounts
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [drawWaveform, colors]);

  return (
    <canvas ref={canvasRef} width="100%" height="100%" {...props}></canvas>
  );
}
